import { Link } from "react-router-dom";
import { course, config } from "@/src/lib/content";

const hero = {
  src: "/images/brand/responsible-architectures-neural-hero.webp",
  srcSmall: "/images/brand/responsible-architectures-neural-hero-sm.webp",
  alt: "A brain-like neural architecture with four connected regions, traceable pathways, a central accountable core, and a governed output gate.",
};
const phases = [
  ["01", "Understand", "Know what an AI system is doing, what it can support, and where confidence is unwarranted."],
  ["02", "Assess", "Trace harms, evidence gaps, fairness concerns, privacy exposure, and adversarial risk across the lifecycle."],
  ["03", "Govern", "Assign decision rights, documentation, oversight, escalation, and accountability to real operating roles."],
  ["04", "Sustain", "Treat energy, carbon, water, infrastructure, model size, and quality tails as architecture constraints."],
  ["05", "Operationalise", "Connect regulation, policy, measurement, and monitoring so responsible practice survives deployment."],
];

export default function HomePage() {
  return (
    <main id="main-content">
      <section className="home-hero">
        <div className="shell home-hero-grid">
          <div className="hero-copy">
            <p className="eyebrow"><a href="https://www.iasaglobal.org/university-programs-atlas/" target="_blank" rel="noopener">A Project Atlas microcredential</a></p>
            <h1>{course.course_name}</h1>
            <p className="hero-subtitle">{course.course_subtitle}</p>
            <p className="hero-intro">Architecture is where responsible AI becomes concrete. This practitioner-led programme moves from critical literacy to lifecycle evidence, governance, sustainability, and accountable operations.</p>
            <div className="button-row">
              <Link className="button primary" to="/programme">Explore the programme</Link>
              <a className="button secondary" href={config.enrolment_contact}>Ask about enrolment</a>
            </div>
            <dl className="hero-facts">
              <div><dt>Format</dt><dd>{course.schedule}</dd></div>
              <div><dt>Core</dt><dd>{course.credential_scope}</dd></div>
              <div><dt>Designed by</dt><dd>{course.developer}, with {course.delivery_partner}</dd></div>
            </dl>
          </div>
          <figure className="hero-visual"><picture><source media="(max-width: 780px)" srcSet={hero.srcSmall} /><img src={hero.src} alt={hero.alt} width="1120" height="1400" fetchPriority="high" /></picture><figcaption>A responsible architecture makes evidence, boundaries, human judgement, and lifecycle impacts visible.</figcaption></figure>
        </div>
      </section>

      <section className="trust-strip" aria-label="Programme positioning">
        <div className="shell trust-grid">
          <p><strong>First in Atlas.</strong> TU Dublin is Iasa’s first university accredited under the Iasa Atlas and BTABoK Accreditation Standards.</p>
          <p><strong>Built for practice.</strong> Five applied labs turn frameworks into evidence, decisions, and operating controls.</p>
          <p><strong>Lifecycle by design.</strong> Responsible AI is treated as an architecture discipline, not a final compliance review.</p>
        </div>
      </section>

      <section className="section shell narrative-section">
        <div className="section-lead">
          <p className="eyebrow">The course argument</p>
          <h2>Responsibility must travel with the system</h2>
          <p>A responsible architecture preserves intent and evidence as an AI system moves from data and model choices into use, monitoring, change, and retirement.</p>
        </div>
        <div className="lifecycle" aria-label="Responsible AI lifecycle: frame, design, evaluate, deploy, observe, improve">
          {[["01", "Frame", "Purpose and affected people"], ["02", "Design", "Constraints and decision rights"], ["03", "Evaluate", "Performance, harm, and trade-offs"], ["04", "Deploy", "Controls and accountable release"], ["05", "Observe", "Signals, incidents, and drift"], ["06", "Improve", "Revalidation and retirement"]].map(([n, title, note]) => <div className="lifecycle-step" key={title}><span>{n}</span><strong>{title}</strong><small>{note}</small></div>)}
        </div>
      </section>

      <section className="section paper-section">
        <div className="shell">
          <div className="split-heading"><div><p className="eyebrow">A deliberate learning arc</p><h2>From discernment to institutional capability</h2></div><p>The sequence alternates concepts, debate, applied analysis, and governed artefacts. Every phase prepares students for a decision they will make in practice.</p></div>
          <div className="phase-grid">
            {phases.map(([number, title, description]) => <article className="phase-card" key={title}><span>{number}</span><h3>{title}</h3><p>{description}</p></article>)}
          </div>
          <p className="section-cta"><Link className="text-link" to="/programme">See all 12 core weeks and the optional capstone extension</Link></p>
        </div>
      </section>

      <section className="section shell partnership">
        <div className="partnership-mark" aria-hidden="true"><span>Iasa</span><i>+</i><span>TU<br />Dublin</span></div>
        <div><p className="eyebrow">Education partnership</p><h2>A professional architecture body, working with a validating university</h2><p>Iasa brings practitioner standards, architecture method, and a global academic pathway. TU Dublin brings institutional validation and a learner-centred university context. Pending specification decisions remain visible rather than being presented as settled.</p><Link className="text-link" to="/about">Understand the teaching and validation context</Link></div>
      </section>

      <section className="section ecosystem-section">
        <div className="shell">
          <div className="section-lead"><p className="eyebrow">One connected ecosystem</p><h2>Learn in the context of architecture practice</h2></div>
          <div className="ecosystem-card-grid">
            {[
              ["Project Atlas", "The Iasa academic programme connecting universities, educators, and architecture pathways.", "https://www.iasaglobal.org/university-programs-atlas/"],
              ["Iasa Global", "The professional association advancing technology architecture as a discipline.", "https://iasaglobal.org"],
              ["Metis", "Iasa’s digital BTABoK knowledge platform for the wider architecture profession.", "https://metis.iasaglobal.org"],
              ["Sustainable Architectures", "A practical architecture toolkit connecting technology decisions with sustainability evidence.", "https://sustainablearchitectures.org"],
            ].map(([title, copy, href]) => <a className="ecosystem-card" href={href} target="_blank" rel="noopener" key={title}><span aria-hidden="true">↗</span><h3>{title}</h3><p>{copy}</p></a>)}
          </div>
        </div>
      </section>

      <section className="cta-band">
        <div className="shell cta-inner"><div><p className="eyebrow">For learners and institutions</p><h2>Build the architecture capability responsible AI needs</h2><p>Ask about the TU Dublin offering, or speak with Iasa about bringing Project Atlas to your institution.</p></div><div className="button-row"><a className="button light" href={config.enrolment_contact}>Enrolment enquiry</a><a className="button outline-light" href="https://www.iasaglobal.org/join-atlas" target="_blank" rel="noopener">Join the Atlas Program</a></div></div>
      </section>
    </main>
  );
}
